import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Progress } from '@backstage/core-components';
import { useApi, discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';

/**
 * Wraps TechDocs reader pages to handle the catalog startup race condition.
 * In dev mode (in-memory SQLite), entities take a few seconds to process after startup.
 * This wrapper polls the entity metadata endpoint and only renders children once the entity exists.
 */
export function TechDocsEntityGate({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [ready, setReady] = useState(false);
  const attemptsRef = useRef(0);
  const maxAttempts = 10;
  const interval = 2000;

  // Extract namespace/kind/name from /docs/:namespace/:kind/:name/*
  const parts = location.pathname.replace(/^\/docs\//, '').split('/');
  const namespace = parts[0];
  const kind = parts[1];
  const name = parts[2];

  const checkEntity = useCallback(async () => {
    if (!namespace || !kind || !name) {
      setReady(true);
      return;
    }
    try {
      const baseUrl = await discoveryApi.getBaseUrl('techdocs');
      const resp = await fetchApi.fetch(
        `${baseUrl}/metadata/entity/${namespace}/${kind}/${name}`,
      );
      if (resp.ok) {
        setReady(true);
      }
    } catch {
      // network error, keep retrying
    }
  }, [discoveryApi, fetchApi, namespace, kind, name]);

  useEffect(() => {
    setReady(false);
    attemptsRef.current = 0;

    // Check immediately
    checkEntity();

    const timer = setInterval(() => {
      attemptsRef.current += 1;
      if (attemptsRef.current >= maxAttempts) {
        // Give up and render anyway (will show Backstage's own error)
        setReady(true);
        clearInterval(timer);
        return;
      }
      checkEntity();
    }, interval);

    return () => clearInterval(timer);
  }, [location.pathname, checkEntity]);

  useEffect(() => {
    if (ready) return;
  }, [ready]);

  if (ready) {
    return <>{children}</>;
  }

  return <Progress />;
}
